import { type Ref, onUnmounted } from 'vue'
import { throttle, throttleTrailing } from '@/utils/optimize'

interface useSliderDragOptions {
  onChange?: (percentage: number) => void
  onEnd?: (percentage: number) => void
}

/** 拖动中 UI 跟随，约 60fps */
const MOVE_THROTTLE_MS = 16
/**
 * 连续提交的合并窗口。窗口内只保留最后一次，前面的值不再发给设备。
 */
export const SLIDER_COMMIT_WAIT_MS = 100

const clampPercent = (clientX: number, rect: DOMRect) => {
  const percentage = ((clientX - rect.left) / rect.width) * 100
  return Math.round(Math.min(100, Math.max(0, percentage)))
}

/**
 * 滑块拖拽
 * @param sliderRef - 组件中定义的滑块容器DOM引用
 * @param onChange - 拖拽过程中值变化的回调（只更新 UI，按下/松手立即触发）
 * @param onEnd - 拖拽结束后的回调（真正提交；连续快速松手会节流，不丢最后一次）
 * @returns startDrag: 开始拖拽方法
 */
export function useSliderDrag(
  sliderRef: Ref<HTMLElement | null>, // 接收组件传入的ref
  options: useSliderDragOptions = {},
) {
  const { onChange, onEnd } = options

  // 只节流 onEnd 的「发出」。stopDrag 本体必须每次都跑：它负责摘 mousemove/mouseup 监听，
  // 若整个 stopDrag 被 leading 节流丢掉一次，松手后 mousemove 仍挂着，滑块会继续跟着鼠标走。
  const emitEnd = throttleTrailing((percentage: number) => {
    onEnd?.(percentage)
  }, SLIDER_COMMIT_WAIT_MS)

  // 更新滑块位置计算逻辑
  const updatePosition = (clientX: number) => {
    if (!sliderRef.value) return
    onChange?.(clampPercent(clientX, sliderRef.value.getBoundingClientRect()))
  }

  const onMouseMove = throttle((e: MouseEvent) => {
    e.preventDefault()
    updatePosition(e.clientX)
  }, MOVE_THROTTLE_MS)

  const stopDrag = (e: MouseEvent) => {
    updatePosition(e.clientX)
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', stopDrag)

    if (onEnd && sliderRef.value) {
      emitEnd(clampPercent(e.clientX, sliderRef.value.getBoundingClientRect()))
    }
  }

  const startDrag = (e: MouseEvent) => {
    e.preventDefault()
    updatePosition(e.clientX)
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', stopDrag)
  }

  // 卸载时清理事件
  onUnmounted(() => {
    emitEnd.cancel()
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', stopDrag)
  })

  return {
    startDrag,
  }
}

/**
 * 短时间连续提交只写最后一次。
 * 安静 SLIDER_COMMIT_WAIT_MS 后把当时的最新值发给设备；等待或写入期间又来的新值会覆盖旧值。
 */
export function useLatestCommit<T>(
  run: (value: T) => Promise<void> | void,
  waitMs = SLIDER_COMMIT_WAIT_MS,
) {
  let timer: ReturnType<typeof setTimeout> | undefined
  let latest: T | undefined
  let hasLatest = false
  let running = false

  async function flush() {
    if (!hasLatest || running) return
    const value = latest as T
    hasLatest = false
    running = true
    try {
      await run(value)
    } catch {
      // 失败由各 action 自己上报；这里只保证后续更新的值仍能继续写。
    } finally {
      running = false
      if (hasLatest) void flush()
    }
  }

  function commit(value: T) {
    latest = value
    hasLatest = true
    if (running) return
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      timer = undefined
      void flush()
    }, waitMs)
  }

  function cancel() {
    if (timer) clearTimeout(timer)
    timer = undefined
    hasLatest = false
  }

  onUnmounted(cancel)

  return { commit, cancel }
}
